/**
 * SPEC-08 §2.4 — density hook.
 *
 * `data-density="comfortable" | "compact"` is mounted on the document
 * root (`<html>`) and persisted to `localStorage` so a power-user
 * preference survives reload. CSS variables in `index.css` consume the
 * value (`--ds-row-py`, `--ds-cell-px`, `--ds-card-p`).
 *
 * Default: `comfortable` (existing layout density), exactly what the UI
 * looks like today. Compact tightens row/card padding for high-volume
 * sampling.
 */

import * as React from "react";

export type Density = "comfortable" | "compact";

const LS_KEY = "ifrs16.ui.density";

function readInitial(): Density {
  if (typeof window === "undefined") return "comfortable";
  try {
    const v = window.localStorage.getItem(LS_KEY);
    if (v === "compact" || v === "comfortable") return v;
  } catch {
    /* localStorage may be unavailable (private mode, tests) — fall through. */
  }
  return "comfortable";
}

export function useDensity(): [Density, (d: Density) => void] {
  const [density, setDensityState] = React.useState<Density>(() =>
    readInitial(),
  );

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-density", density);
    try {
      window.localStorage.setItem(LS_KEY, density);
    } catch {
      /* no-op */
    }
  }, [density]);

  const setDensity = React.useCallback((d: Density) => setDensityState(d), []);

  return [density, setDensity];
}
